import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, catchError, of, map } from "rxjs";
import { GetUserSessionIdReturnCodeEnum, SendConfirmationCodeReturnCodeEnum, VerificationTypeEnum, VerifyConfirmationCodeReturnCodeEnum } from "../shared/enums";
import { UserSession } from "../shared/userSession";
import { ConfirmationChannel } from "../shared/confirmationChannel";
import { PrivacyConsentService } from "./privacy-consent.service";


@Injectable({ providedIn: "root" })
export class UserService {

    settings = environment;
    http: HttpClient;
    privacyConsentService: PrivacyConsentService;

    constructor(http: HttpClient, privacyConsentService: PrivacyConsentService) {
        this.http = http;
        this.privacyConsentService = privacyConsentService;
    }

    private _userSession: UserSession | null = null;

    get UserSession(): UserSession | null {

        if (this._userSession !== null) {

            return this._userSession;
        }
        else {

            let storedSession: UserSession = JSON.parse(sessionStorage.getItem("userSession") ?? "{}");

            //if (Object.keys(storedSession).length !== 0) {

            this._userSession = Object.assign(new UserSession({}), storedSession);
            //}
        }

        return this._userSession;
    }

    set UserSession(value: UserSession | null) {

        this._userSession = value;

        if (this._userSession === null) {

            sessionStorage.removeItem("userSession");
        }
        else {

            sessionStorage.setItem("userSession", JSON.stringify(this._userSession));
        }
    }


    getIdToken(firstName: string, lastName: string, phone: string, email: string | null): Observable<any> {

        const headers = new HttpHeaders({
            'UserSessionId': this.UserSession!.UserSessionId,
        });

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/GetIdToken"
            , {
                firstName: firstName,
                lastName: lastName,
                email: email,
                phone: phone
            }, { headers: headers }
        ).pipe(
            map((response: any) => {

                return response;
            }),
            catchError(error => {
                console.error("Error occurred:", JSON.stringify(error, null, 2));
                return of({ returnCode: 1, message: 'Custom error message' });
            })
        );
    }

    checkMobile(phone: string): Observable<any> {
        const headers = new HttpHeaders({
            'UserSessionId': this.UserSession!.UserSessionId,
        });

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/CheckMobile"
            , { mobilePhone: phone }, { headers: headers })
            .pipe(
                map((response: any) => {

                    return response;
                }),
                catchError(error => {
                    console.error("Error occurred:", JSON.stringify(error, null, 2));
                    return of({ returnCode: 1, message: 'Custom error message' });
                })
            );
    }

    checkEmail(email: string): Observable<any> {
        const headers = new HttpHeaders({
            'UserSessionId': this.UserSession!.UserSessionId,
        });

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/CheckEmail"
            , { email: email }, { headers: headers })
            .pipe(
                map((response: any) => {

                    return response;
                }),
                catchError(error => {
                    console.error("Error occurred:", JSON.stringify(error, null, 2));
                    return of({ returnCode: 1, message: 'Custom error message' });
                })
            );
    }

    uploadDoc(file: File | null, role: number, roleDesription: string): Observable<any> {

        const formData = new FormData();

        if (file != null) {

            formData.append('file', file, file.name);
        }

        formData.append('role', role.toString());

        formData.append('roleDescription', roleDesription);

        const headers = new HttpHeaders({
            'UserSessionId': this.UserSession!.UserSessionId,
        });

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/UploadDoc", formData, {
            headers: headers,
        }).pipe(map((response: any) => {

            return response;
        }), catchError(error => {
            console.error("Error occurred:", JSON.stringify(error, null, 2));
            return of({ returnCode: 1, message: 'Custom error message' });
        }));
    }

    getAuthUserSessionId(id: string, sugTaagid: number, misparTaagid: string, disclaimer: boolean, declaration: boolean): Observable<any> {

        this.UserSession!.Id = id;
        this.UserSession!.SugTaagid = sugTaagid;
        this.UserSession!.MisparTaagid = misparTaagid;
        this.UserSession!.Disclaimer = disclaimer;
        this.UserSession!.Declaration = declaration;

        const headers = new HttpHeaders({
            'Authorization': "Bearer " + this.UserSession!.Tkn,
        });

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/GetAuthUserSessionId"
            , {
                Id: id,
                SugTaagid: sugTaagid,
                MisparTaagid: misparTaagid,
                Disclaimer: disclaimer,
                Declaration: declaration,
                Cookies: this.privacyConsentService.accepted(),
            }, { headers: headers }).pipe(
                map((response: any) => {
                    response.returnCode = GetUserSessionIdReturnCodeEnum[response.returnCode] !== undefined ? response.returnCode : GetUserSessionIdReturnCodeEnum.Error;

                    this.UserSession!.GoogleHashTag = response.googleHashTag;

                    this.UserSession!.GetUserSessionIdReturnCode = response.returnCode;

                    this.UserSession!.UserSessionId = response.sessionId;

                    this.UserSession!.IdConfirmChannels = response.confirmChannels?.map((channel: any) => {
                        return Object.assign(new ConfirmationChannel(), channel);
                    });

                    this.UserSession!.FirstName = response.firstName;
                    this.UserSession!.LastName = response.lastName;
                    this.UserSession!.Email = response.email;
                    this.UserSession!.PhoneNumber = response.phoneNumber;

                    if (this.UserSession!.GoogleHashTag != null) {
                        (window as any).dataLayer.push({
                            'event': 'userID',
                            'user_id': this.UserSession!.GoogleHashTag
                        });
                    }

                    return response;
                }),
                catchError(error => {
                    console.error("Error occurred:", JSON.stringify(error, null, 2));

                    this.UserSession!.GetUserSessionIdReturnCode = GetUserSessionIdReturnCodeEnum.Error;

                    return of({ returnCode: GetUserSessionIdReturnCodeEnum.Error, message: 'Custom error message' });
                })
            );
    }

    getUserSessionId(id: string, sugTaagid: number, misparTaagid: string, disclaimer: boolean, declaration: boolean, token: string): Observable<any> {

        this.UserSession = new UserSession({ id: id, sugTaagid: sugTaagid, misparTaagid: misparTaagid, disclaimer: disclaimer, declaration: declaration });

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/GetUserSessionId"
            , {
                Id: id,
                SugTaagid: sugTaagid,
                MisparTaagid: misparTaagid,
                Disclaimer: disclaimer,
                Declaration: declaration,
                Cookies: this.privacyConsentService.accepted(),
                RecaptchaToken: token
            }).pipe(
                map((response: any) => {
                    response.returnCode = GetUserSessionIdReturnCodeEnum[response.returnCode] !== undefined ? response.returnCode : GetUserSessionIdReturnCodeEnum.Error;

                    this.UserSession!.GoogleHashTag = response.googleHashTag;

                    this.UserSession!.GetUserSessionIdReturnCode = response.returnCode;

                    this.UserSession!.UserSessionId = response.sessionId;

                    this.UserSession!.IdConfirmChannels = response.confirmChannels?.map((channel: any) => {
                        return Object.assign(new ConfirmationChannel(), channel);
                    });

                    if (response.returnCode == GetUserSessionIdReturnCodeEnum.OneChannelBusiness
                        || response.returnCode == GetUserSessionIdReturnCodeEnum.OneChannelDigitel
                        || response.returnCode == GetUserSessionIdReturnCodeEnum.OneChannelOther) {

                        this.UserSession!.ConfirmChannel = this.UserSession!.IdConfirmChannels[0];
                        this.UserSession!.VerificationType = VerificationTypeEnum.Id;
                        this.UserSession!.VerificationCodeRedirectUrl = "/business-reg/detail-page";
                    }

                    if (this.UserSession!.GoogleHashTag != null) {
                        (window as any).dataLayer.push({
                            'event': 'userID',
                            'user_id': this.UserSession!.GoogleHashTag
                        });
                    }

                    return response;
                }),
                catchError(error => {
                    console.error("Error occurred:", JSON.stringify(error, null, 2));

                    this.UserSession!.GetUserSessionIdReturnCode = GetUserSessionIdReturnCodeEnum.Error;

                    return of({ returnCode: GetUserSessionIdReturnCodeEnum.Error, message: 'Custom error message' });
                })
            );
    }

    sendConfirmationCode(confirmChannel: ConfirmationChannel, verificationType: number, verificationCodeRedirectUrl: string): Observable<any> {

        this.UserSession!.ConfirmChannel = confirmChannel;
        this.UserSession!.VerificationType = verificationType;
        this.UserSession!.VerificationCodeRedirectUrl = verificationCodeRedirectUrl;

        const headers = new HttpHeaders({
            'UserSessionId': this.UserSession!.UserSessionId,
        });

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/SendConfirmationCode", {
            confirmationChannel: confirmChannel,
            verificationType: verificationType
        }, { headers: headers }).pipe(
            map((response: any) => {
                return response;
            }),
            catchError(error => {
                console.error("Error occurred:", JSON.stringify(error, null, 2));
                return of({ returnCode: SendConfirmationCodeReturnCodeEnum.Error, message: 'Custom error message' });
            })
        );
    }

    verifyConfirmationCode(otpCode: string): Observable<any> {

        const headers = new HttpHeaders({
            'UserSessionId': this.UserSession!.UserSessionId,
        });

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/VerifyConfirmationCode", {
            confirmationChannel: this.UserSession!.ConfirmChannel,
            verificationType: this.UserSession!.VerificationType,
            code: otpCode
        }, { headers: headers }).pipe(
            map((response: any) => {

                this.UserSession!.VerifyConfirmationCodeReturnCode = response.returnCode;

                if (response.returnCode == VerifyConfirmationCodeReturnCodeEnum.Valid) {

                    switch (this.UserSession!.VerificationType) {
                        case VerificationTypeEnum.Id:

                            this.UserSession!.FirstName = response.firstName;
                            this.UserSession!.LastName = response.lastName;
                            this.UserSession!.Email = response.email;
                            this.UserSession!.PhoneNumber = response.phoneNumber;
                            break;
                        case VerificationTypeEnum.Email:

                            this.UserSession!.Email = response.email;
                            this.UserSession!.ConfirmedEmail = response.email;
                            this.UserSession!.TmpEmail = null;
                            break;
                        case VerificationTypeEnum.Phone:

                            this.UserSession!.PhoneNumber = response.phoneNumber;
                            this.UserSession!.ConfirmedPhone = response.phoneNumber
                            this.UserSession!.TmpPhone = null;
                            break;
                    }
                }

                return response;
            }),
            catchError(error => {
                console.error("Error occurred:", JSON.stringify(error, null, 2));
                return of({ returnCode: SendConfirmationCodeReturnCodeEnum.Error, message: 'Custom error message' });
            })
        );
    }

    registerBusiness(appOk: boolean, whatsappOk: boolean, smsOk: boolean, emailOk: boolean): Observable<any> {

        let headers = null;

        if (this.UserSession!.DbCoreToken != null && this.UserSession!.DbCoreToken != "") {
            headers = new HttpHeaders({
                'UserSessionId': this.UserSession!.UserSessionId,
                'DbCoreToken': this.UserSession!.DbCoreToken!
            });
        }
        else {
            headers = new HttpHeaders({
                'UserSessionId': this.UserSession!.UserSessionId
            });
        }

        return this.http.post(this.settings.backEndServer.apiBaseUrl + "/User/RegisterBusiness", {

            appOk: appOk,
            whatsappOk: whatsappOk,
            smsOk: smsOk,
            emailOk: emailOk,
            callTime: this.UserSession?.CallTime
        }, { headers: headers }).pipe(
            map((response: any) => {

                if (response.returnCode == 0 || response.returnCode == 2) {

                    this.UserSession!.IsRep = response.isRep;
                }

                return response;
            }),
            catchError(error => {
                console.error("Error occurred:", JSON.stringify(error, null, 2));
                return of({ returnCode: 1, message: 'Custom error message' });
            })
        );
    }
}